import { IconButton, TableCell } from '@mui/material';
import { SerialBond } from '../debt-service-page.tsx';
import { DragIndicator, Delete } from '@mui/icons-material';
import moment from 'moment';
import formatter from "../../../tools/formatter.js";
import { DealBuilder } from '../../services/deal-builder.ts';
import useDealBuilderResultStore from '../../../stores/deal-builder-result.store.ts';
import { useDragAndDropContext } from '../bonds-page/drag-and-drop-context.tsx';
import DragAndDropRow from '../bonds-page/drag-and-drop-row.tsx';
import MaturityPicker from '../bonds-page/maturity-picker.tsx';
import React from 'react';
import CurrencyInput from '../bonds-page/currency-input.tsx';

interface SerialBondRow {
  builder: DealBuilder,
  bond: SerialBond,
}

export default function SerialBondRow({ 
  builder, 
  bond,
}: SerialBondRow) {
  const { setSource, setTarget, isCurrentSource, getTargetStatus } = useDragAndDropContext();
  const { setDealBuilderService } = useDealBuilderResultStore();
  const production = builder.production.find(x => x.bond === bond);
  const isDragging = isCurrentSource({ bond });
  const targetStatus = getTargetStatus(builder, { bond });
  
  function updateTarget(e: React.DragEvent) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setTarget({ bond, position: "middle" });
  }

  return (
    <DragAndDropRow
      dragging={isDragging} 
      onDragOver={updateTarget}
      indicator={targetStatus}
    >
      <TableCell width="3px" sx={{ p: 0 }}></TableCell>
      <TableCell width="70px">
        <IconButton
          draggable
          onDragStart={() => setSource({ bond })}
          onDragEnd={() => setSource(undefined)}
        >
          <DragIndicator fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell>
        <MaturityPicker
          format="MM/DD/YYYY"
          value={moment(bond.maturity)}
          firstCouponDate={builder.firstCouponDate}
          onChange={async (value) => {
            if (value.isSame(moment(bond.maturity), "day")) return;
            builder.updateBondDate(bond, value);
            builder.updateDebtService();
            setDealBuilderService(builder);
            await builder.updateProduction(builder.bonds);
            setDealBuilderService(builder);
          }}
        />
      </TableCell>
      <TableCell align="right">
        <CurrencyInput
          className={"switch-db-0"}
          value={bond.principal}
          size="small"
          startAdornment="$"
          locked={bond.lockedPrincipal}
          onLockedChange={(val) => {
            bond.lockedPrincipal = val;
            setDealBuilderService(builder);
          }}
          onValidChange={async (value) => {
            const newPrincipal = value * 1000;
            if (bond.principal === newPrincipal || bond.principal === value) return;
            const diff = newPrincipal - bond.principal;
            builder.totalTargetParAmount += diff;
            bond.principal = newPrincipal;
            bond.lockedPrincipal = true;
            builder.updateDebtService();
            setDealBuilderService(builder);
            await builder.updateProduction(builder.bonds);
            setDealBuilderService(builder);
          }}
          isValueValid={(value) => {
            const valid = (value * 1000) % builder.denomination === 0;
            if(valid) return { valid: true };
            return { valid: false, error: "The principal must be divisible by the denomination." };
          }}
        />
      </TableCell>
      <TableCell align="right">
        <CurrencyInput
          className={"switch-db-1"}
          decimalScale={3}
          size="small"
          value={bond.coupon * 100}
          endAdornment="%"
          locked={bond.lockedCoupon}
          onLockedChange={(val) => {
            bond.lockedCoupon = val;
            setDealBuilderService(builder);
          }}
          onValidChange={async (value) => {
            const diff = bond.coupon - value / 100;
            if (Math.abs(diff) < 0.00001) return;
            bond.coupon = value / 100
            bond.lockedCoupon = true;
            builder.updateDebtService();
            setDealBuilderService(builder);
            await builder.updateProduction(builder.bonds);
            setDealBuilderService(builder);
          }}
          isValueValid={() => ({ valid: true })}
        />
      </TableCell>

      {production ? (
        <React.Fragment>
          <TableCell align="right">{formatter.percent(production.curve, 3)}</TableCell>

          <TableCell align="right">
            <CurrencyInput
              decimalScale={3}
              className={"switch-db-2"}
              value={production.spread * 100}
              endAdornment="%"
              size="small"
              locked={production.locked}
              onLockedChange={async (val) => {
                production.locked = val;
                setDealBuilderService(builder);
              }}
              onValidChange={async (value) => {
                const diff = production.spread - value / 100;
                if (Math.abs(diff) < 0.00001) return;
                production.locked = true;
                production.spread = value / 100;
                await builder.updateProduction();
                setDealBuilderService(builder);
              }}
              isValueValid={() => ({ valid: true })}
            />
          </TableCell>

          <TableCell align="right">
            <CurrencyInput
              decimalScale={3}
              className={"switch-db-3"}
              value={(production.spread + production.curve) * 100}
              endAdornment="%"
              size="small"
              locked={production.locked}
              onLockedChange={(val) => {
                production.locked = val;
                setDealBuilderService(builder);
              }}
              onValidChange={async (value) => {
                const diff = (value / 100) - production.spread - production.curve;
                if (Math.abs(diff) < 0.00001) return;
                production.locked = true;
                production.spread += diff;
                await builder.updateProduction();
                setDealBuilderService(builder);
              }}
              isValueValid={() => ({ valid: true })}
            />
          </TableCell>

          <TableCell align="right">
            {formatter.percent(production.ytm, 3)}
          </TableCell>

          <TableCell align="right">
            {formatter.money(production.price, true)}
          </TableCell>

          <TableCell align="right">{formatter.money(production.production, false)}</TableCell>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">N/A</TableCell>
          <TableCell align="right">N/A</TableCell>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">-</TableCell>
          <TableCell align="right">N/A</TableCell>
        </React.Fragment>
      )}

      <TableCell align="center">
        <IconButton 
          size="small"
          sx={{ color: 'error.main', '&:hover': { color: 'error.dark' } }}
          onClick={async () => {
            builder.deleteBond(bond);
            builder.updateDebtService();
            await builder.updateProduction();
            setDealBuilderService(builder);
          }}
        >
          <Delete fontSize="small" />
        </IconButton>
      </TableCell>
    </DragAndDropRow>
  );
};
